import {
  Box,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  QuestionMark,
  Person,
  Person2,
} from "@mui/icons-material";
import { Tables } from "../../database.types";

interface ClassicBonusRoundPlayerProps {
  bonusRound: Tables<"bonus_rounds">;
  classicAnswers: Tables<"classic_bonus_answers">[];
  teamResponses: Tables<"classic_bonus_team_responses">[];
  team1Id: string;
  team2Id: string;
  team1Name: string;
  team2Name: string;
}

const ClassicBonusRoundPlayer = (props: ClassicBonusRoundPlayerProps) => {
  const {
    bonusRound,
    classicAnswers,
    teamResponses,
    team1Id,
    team2Id,
    team1Name,
    team2Name,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const team1Responses = teamResponses.filter((r) => r.team_id === team1Id);
  const team2Responses = teamResponses.filter((r) => r.team_id === team2Id);

  return (
    <Paper
      elevation={3}
      sx={{
        width: "100%",
        display: "flex",
        flex: 1,
        flexDirection: "column",
        borderRadius: 2,
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          width: "100%",
          p: "16px",
        }}
      >
        {/* Question and Answers */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 2, sm: 3 },
            order: isMobile ? 1 : 2,
            flex: 1,
          }}
        >
          {/* Question */}
          {bonusRound.show_question && (
            <Typography
              variant={isMobile ? "h6" : "h5"}
              color="text.primary"
              sx={{ wordBreak: "break-word", textAlign: "center" }}
            >
              {bonusRound.question_text}
            </Typography>
          )}

          {/* Answers Grid + scores on sides */}
          <Box display="flex" height="100%">
            {/* Team 1 Score */}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "row" : "column",
                gap: 1,
                minWidth: isMobile ? "auto" : "60px",
                alignItems: "center",
                px: 1,
                py: 4,
                alignSelf: isMobile ? "center" : "stretch",
              }}
            >
              <Chip
                icon={<Person />}
                label={team1Name}
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  mb: 1,
                  display: { xs: "flex", sm: "none" },
                  fontWeight: "medium",
                }}
              />

              {team1Responses.map((r, index) => {
                const isCorrect = classicAnswers.some(
                  (a) =>
                    a.correct_team_id === team1Id &&
                    a.answer_text.toLowerCase().trim() ===
                      r.response_text.toLowerCase().trim()
                );
                return (
                  <Tooltip
                    key={index}
                    title={isCorrect ? "Correct" : "Incorrect"}
                    arrow
                  >
                    <Box
                      sx={{
                        height: { xs: "24px", sm: "30px" },
                        width: { xs: "24px", sm: "30px" },
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isCorrect
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                        color: "white",
                      }}
                    >
                      {isCorrect ? (
                        <CheckCircle
                          sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                        />
                      ) : (
                        <Cancel
                          sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>

            {/* Answers Grid */}
            <Box
              sx={{
                width: "100%",
                overflow: "auto",
                p: 1,
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: { xs: 1, sm: 1.5 },
                  overflow: "auto",
                  p: 1,
                }}
              >
                {classicAnswers.map((item, index) => {
                  const isTeam1 = item.correct_team_id === team1Id;

                  return (
                    <Paper
                      key={index}
                      elevation={2}
                      sx={{
                        height: { xs: 45, sm: 80 },
                        width: { xs: 80, sm: 110, md: 150 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 1,
                        flexShrink: 0,
                        borderRadius: 1,
                        bgcolor: item.correct_team_id
                          ? isTeam1
                            ? theme.palette.primary.main
                            : theme.palette.secondary.main
                          : "rgba(0, 0, 0, 0.08)",
                        color: item.correct_team_id
                          ? "white"
                          : "text.secondary",
                        transition: "transform 0.2s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      {item.correct_team_id || bonusRound.is_reveal_all ? (
                        <Typography
                          variant="h3"
                          sx={{
                            width: "100%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textAlign: "center",
                            fontWeight: "medium",
                            fontSize: { xs: 11, sm: 14, md: 20 },
                          }}
                        >
                          {item.answer_text}
                        </Typography>
                      ) : (
                        <QuestionMark fontSize="small" />
                      )}
                    </Paper>
                  );
                })}
              </Box>
            </Box>

            {/* Team 2 Score */}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "row" : "column",
                gap: 1,
                minWidth: isMobile ? "auto" : "60px",
                alignItems: "center",
                px: 1,
                py: 4,
                alignSelf: isMobile ? "center" : "stretch",
              }}
            >
              <Chip
                icon={<Person2 />}
                label={team2Name}
                color="secondary"
                variant="outlined"
                size="small"
                sx={{
                  mb: 1,
                  display: { xs: "flex", sm: "none" },
                  fontWeight: "medium",
                }}
              />

              {team2Responses.map((r, index) => {
                const isCorrect = classicAnswers.some(
                  (a) =>
                    a.correct_team_id === team2Id &&
                    a.answer_text.toLowerCase().trim() ===
                      r.response_text.toLowerCase().trim()
                );
                return (
                  <Tooltip
                    key={index}
                    title={isCorrect ? "Correct" : "Incorrect"}
                    arrow
                  >
                    <Box
                      sx={{
                        height: { xs: "24px", sm: "30px" },
                        width: { xs: "24px", sm: "30px" },
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isCorrect
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                        color: "white",
                      }}
                    >
                      {isCorrect ? (
                        <CheckCircle
                          sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                        />
                      ) : (
                        <Cancel
                          sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default ClassicBonusRoundPlayer;
